import AgoraRTC, {
  AgoraRTCReactError, ICameraVideoTrack, IMicrophoneAudioTrack,
  useLocalCameraTrack, useLocalMicrophoneTrack
} from 'agora-rtc-react';
import { Dispatch, SetStateAction, createContext, useContext, useEffect, useState } from 'react';

import { CameraFacing } from '@/app/camera/facingIntervalBuffer';
import useChangeDeviceForLocalTrack, { ChangeDeviceProps, LocalCameraTrack, LocalMicrophoneTrack } from '@/app/hooks/useChangeDeviceForLocalTrack';
import useHighResAdjustedVideoProfile from '@/app/hooks/useHighResAdjustedVideoProfile';
import useDeviceToggle from './hooks/useDeviceToggle';
import { useSingleTrackInstance } from './hooks/useSingleTrackInstance';
import { Permissions } from './types';

export interface TrackDeviceGroup<A, B> {
  track: A | null,
  isLoading: boolean,
  isEnabled: boolean,
  error: AgoraRTCReactError | null,
  device: MediaDeviceInfo['deviceId'] | undefined,
  isPermissionGranted: boolean;
  changeDevice(deviceOptions: B): Promise<boolean>,
  toggleDevice(): Promise<void>,
}

interface SpeakerDevice {
  devices: MediaDeviceInfo[],
  deviceId: MediaDeviceInfo['deviceId'] | undefined,
  changeDevice(deviceId: string): void,
}

export interface IAppContext {
  camera: TrackDeviceGroup<ICameraVideoTrack, ChangeDeviceProps>,
  microphone: TrackDeviceGroup<IMicrophoneAudioTrack, MediaDeviceInfo['deviceId']>,
  speaker: SpeakerDevice,
  // Derived from cameraFacing, never stored: a stored copy is how the settings
  // camera picker left the rear view mirrored (CAD-6931). Only a known front
  // camera reads like a mirror — a wrongly mirrored yard misdirects the
  // walkthrough, while an unmirrored face is merely unusual.
  isMainVideoMirrored: boolean,
  // Which way the camera points, as a record rather than a rendering decision:
  // null once something changed the camera without saying which way it now
  // faces, so nothing downstream labels footage it cannot vouch for.
  cameraFacing: CameraFacing | null,
  setCameraFacing: Dispatch<SetStateAction<CameraFacing | null>>,
}

export const AppContext = createContext<IAppContext>(null!);

function useCamera(isPermissionGranted?: boolean, isHost?: boolean): TrackDeviceGroup<ICameraVideoTrack, ChangeDeviceProps> {
  const encoderConfig = useHighResAdjustedVideoProfile(isHost);

  const {
    localCameraTrack,
    ...cameraOptions
  } = useLocalCameraTrack(isPermissionGranted === true, { encoderConfig });

  const {
    localTrack,
    isLoading,
    error
  } = useSingleTrackInstance(localCameraTrack, cameraOptions);

  const { isEnabled, toggleDevice } = useDeviceToggle(
    localTrack as LocalCameraTrack, 'video'
  );

  // Rotating the phone mid-call flips the camera aspect and with it the
  // encoder profile (portrait must PUBLISH portrait — a stale landscape
  // profile makes the SDK center-crop the frame, see CAD-6855). The track is
  // created once, so profile changes are re-applied to the live track here.
  // Only to a live one, though: the SDK rejects encoder changes on a disabled
  // track, so while the camera is off the profile waits for isEnabled to flip
  // back and re-applies then.
  useEffect(() => {
    const cameraTrack = localTrack as ICameraVideoTrack | null;
    if (!isEnabled || typeof cameraTrack?.setEncoderConfiguration !== 'function') {
      return;
    }
    cameraTrack.setEncoderConfiguration(encoderConfig).catch(() => undefined);
  }, [localTrack, encoderConfig, isEnabled]);

  const { device, changeDevice } = useChangeDeviceForLocalTrack(
    localTrack as LocalCameraTrack, isLoading, error
  );

  return {
    track: localTrack,
    isEnabled,
    toggleDevice,
    changeDevice,
    device,
    error,
    isPermissionGranted: isPermissionGranted === true,
    isLoading
  }
}

function useMicrophone(isPermissionGranted?: boolean): TrackDeviceGroup<IMicrophoneAudioTrack, MediaDeviceInfo['deviceId']> {
  const {
    localMicrophoneTrack,
    ...microphoneOptions
  } = useLocalMicrophoneTrack(isPermissionGranted === true);

  const {
    localTrack,
    isLoading,
    error,
  } = useSingleTrackInstance(localMicrophoneTrack, microphoneOptions);

  const { device, changeDevice } = useChangeDeviceForLocalTrack(
    localTrack as LocalMicrophoneTrack, isLoading, error
  );

  const { isEnabled, toggleDevice } = useDeviceToggle(
    localTrack as LocalMicrophoneTrack, 'audio'
  );

  return {
    track: localTrack,
    isEnabled,
    toggleDevice,
    changeDevice,
    device,
    error,
    isPermissionGranted: isPermissionGranted === true,
    isLoading
  }
}

function useSpeaker(): SpeakerDevice {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<MediaDeviceInfo['deviceId']>();

  useEffect(function fetchInitialSpeakerDevices() {
    AgoraRTC.getPlaybackDevices().then(playbackDevices => {
      setDevices(playbackDevices);
    }, (error) => {
      console.error('Failed to fetch playback devices', error);
    });
  }, []);

  useEffect(function setInitialSpeaker() {
    if (devices.length) {
      setDeviceId(devices[0].deviceId);
    }
  }, [devices]);

  const changeDevice = (deviceId: string) => {
    setDeviceId(deviceId);
  }

  return {
    devices,
    deviceId,
    changeDevice
  }
}

export function usePrepareAppState(permissions: Permissions, isHost?: boolean): IAppContext {
  const camera = useCamera(permissions.video, isHost);
  const microphone = useMicrophone(permissions.audio);
  const speaker = useSpeaker();

  // Assumed, not observed: browsers open the front camera by default, and
  // nothing confirms it until the customer flips.
  const [cameraFacing, setCameraFacing] = useState<CameraFacing | null>('user');

  return {
    camera,
    microphone,
    speaker,
    isMainVideoMirrored: cameraFacing === 'user',
    cameraFacing,
    setCameraFacing,
  };
}

export function useAppContext(): IAppContext {
  return useContext(AppContext);
}
